/**
 * Mapping centralisé entre nos clés internes de plans (utilisées partout
 * dans le code, dans `billingApi.config()`, dans `subscriptions.plan`, etc.)
 * et les identifiants de produits Apple In-App Purchase.
 *
 * IMPORTANT — Ces identifiants DOIVENT correspondre EXACTEMENT à ce qui est
 * configuré dans App Store Connect (Achats intégrés → Abonnements
 * auto-renouvelables) et dans RevenueCat (Products).
 *
 * Prix de référence (incluant +15% par rapport à Stripe pour absorber la
 * commission Apple Small Business 15%) :
 *   Discovery (hebdo)  : 11,99 $ / sem
 *   Solo mensuel       : 34,99 $ / mois
 *   Solo annuel        : 334,99 $ / an  (tier App Store Connect)
 *   Team mensuel       : 69,99 $ / mois
 *   Team annuel        : 671,99 $ / an  (tier App Store Connect)
 *   Pro mensuel        : 114,99 $ / mois
 *   Pro annuel         : 1 099,99 $ / an (tier App Store Connect)
 */

export const APPLE_PRODUCT_IDS = {
  weekly_solo:  'sp_discovery_weekly',
  monthly_solo: 'sp_solo_monthly',
  annual_solo:  'sp_solo_yearly',
  monthly_team: 'sp_team_monthly',
  annual_team:  'sp_team_yearly',
  monthly_pro:  'sp_pro_monthly',
  annual_pro:   'sp_pro_yearly',
};

/**
 * Prix d'affichage sur iOS natif (+15% par rapport à Stripe pour absorber
 * la commission Apple Small Business 15%). Doit correspondre EXACTEMENT
 * aux prix configurés dans App Store Connect.
 *
 * Format : { amount: prix affiché /période, monthly_equivalent: ÷12 pour annuel,
 *            savings: économie en $ pour les plans annuels }
 */
export const APPLE_DISPLAY_PRICES = {
  weekly_solo:  { amount: 12.99 },
  monthly_solo: { amount: 34.99 },
  annual_solo:  { amount: 334.99, monthly_equivalent: 27.92, savings: 84.89 },
  monthly_team: { amount: 69.99 },
  annual_team:  { amount: 671.99, monthly_equivalent: 56.00, savings: 167.89 },
  monthly_pro:  { amount: 114.99 },
  annual_pro:   { amount: 1099.99, monthly_equivalent: 91.67, savings: 279.89 },
};

/** Métadonnées statiques pour afficher les plans IAP sans appeler le backend. */
const IOS_PLAN_META = {
  weekly_solo:  { tier: 'solo', interval: 'week',  seats: 1,  label: 'Découverte' },
  monthly_solo: { tier: 'solo', interval: 'month', seats: 1,  label: 'Solo' },
  annual_solo:  { tier: 'solo', interval: 'year',  seats: 1,  label: 'Solo' },
  monthly_team: { tier: 'team', interval: 'month', seats: 5,  label: 'Équipe', popular: true },
  annual_team:  { tier: 'team', interval: 'year',  seats: 5,  label: 'Équipe', popular: true },
  monthly_pro:  { tier: 'pro',  interval: 'month', seats: 10, label: 'Pro Illimité' },
  annual_pro:   { tier: 'pro',  interval: 'year',  seats: 10, label: 'Pro Illimité' },
};

/**
 * Config de repli pour Capacitor iOS quand billingApi.config() échoue
 * (réseau, reviewer offline, etc.) — évite une page /pricing vide sur iPad.
 */
export function buildNativeIosFallbackConfig() {
  const plans = {};
  Object.entries(APPLE_DISPLAY_PRICES).forEach(([planKey, priceData]) => {
    plans[planKey] = { ...IOS_PLAN_META[planKey], ...priceData };
  });
  return { trial_days: 3, currency: 'CAD', plans };
}

/** Inverse — utile pour mapper un product ID Apple vers notre clé interne. */
export const PLAN_KEY_FROM_APPLE_ID = Object.fromEntries(
  Object.entries(APPLE_PRODUCT_IDS).map(([planKey, appleId]) => [appleId, planKey])
);

export function applePlanIdFor(planKey) {
  return APPLE_PRODUCT_IDS[planKey] || null;
}

/**
 * Convertit une config Stripe (billingApi.config().plans) en config Apple
 * en remplaçant les prix par les prix +15% destinés à l'App Store.
 * Conserve les autres métadonnées (nom, description, etc.).
 */
export function applyApplePricing(stripeConfig) {
  if (!stripeConfig?.plans) return stripeConfig;
  const plans = { ...stripeConfig.plans };
  Object.entries(APPLE_DISPLAY_PRICES).forEach(([planKey, priceData]) => {
    if (plans[planKey]) {
      plans[planKey] = { ...plans[planKey], ...priceData };
    }
  });
  return { ...stripeConfig, plans };
}
